/**
 * Main layout component with navigation.
 */

import type { ReactNode } from 'react'
import { Link, useLocation } from 'wouter'
import { useBackButton } from '@tma.js/sdk-react'
import { useEffect } from 'react'
import {
  Home,
  MessageCircle,
  CheckCircle,
  Users,
  BarChart3,
  Settings,
} from 'lucide-react'
import clsx from 'clsx'

const navItems = [
  { path: '/', icon: Home, label: '' },
  { path: '/dialogs', icon: MessageCircle, label: '' },
  { path: '/approvals', icon: CheckCircle, label: '' },
  { path: '/team', icon: Users, label: '' },
  { path: '/analytics', icon: BarChart3, label: '' },
  { path: '/settings', icon: Settings, label: '' },
]

export function Layout({ children }: { children: ReactNode }) {
  const [location] = useLocation()
  const backButton = useBackButton()

  useEffect(() => {
    // Show back button only on non-home pages
    if (location !== '/') {
      backButton.show()
    } else {
      backButton.hide()
    }
  }, [location, backButton])

  return (
    <div className="flex flex-col min-h-screen bg-tg-bg">
      {/* Main content */}
      <main className="flex-1 pb-20 safe-area-top">
        {children}
      </main>

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-tg-section border-t border-tg-separator safe-area-bottom">
        <div className="flex items-center justify-around h-16">
          {navItems.map(({ path, icon: Icon, label }) => (
            <Link
              key={path}
              href={path}
              className={(isActive) =>
                clsx(
                  'flex flex-col items-center justify-center w-full h-full tap-highlight-transparent transition-colors',
                  isActive ? 'text-tg-button' : 'text-tg-hint'
                )
              }
            >
              <Icon className="w-6 h-6" />
              {label && (
                <span className="text-[10px] mt-0.5">{label}</span>
              )}
            </Link>
          ))}
        </div>
      </nav>
    </div>
  )
}
